import { useState, useEffect, useMemo } from "react"
import { Link } from "react-router-dom"
import { projectsApi, topicsApi, authApi, schoolsApi, coursesApi } from "../../../services/api"
import Button from "../../../components/ui/Button"
import Card, { CardBody } from "../../../components/ui/Card"
import { Field, Input, Select, Textarea } from "../../../components/ui/Field"
import { cn } from "../../../utils/cn"

const DOCUMENTATION_TYPES = [
  'Undergraduate Final Year Project',
  "Master's Thesis",
  'PhD Thesis',
  'PGD Final Project'
]

const STEPS = [
  { id: 1, title: 'Topic', subtitle: 'Choose an idea or write yours' },
  { id: 2, title: 'School', subtitle: 'Confirm your details' },
  { id: 3, title: 'Type', subtitle: 'Pick a project category' }
]

function Stepper({ currentStep, onStepClick }) {
  const idx = Math.max(0, Math.min(STEPS.length - 1, (currentStep || 1) - 1))
  const progressPct = STEPS.length <= 1 ? 0 : (idx / (STEPS.length - 1)) * 100

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between gap-3">
        {STEPS.map((s, i) => {
          const isCompleted = currentStep > s.id
          const isCurrent = currentStep === s.id
          const canClick = s.id < currentStep

          return (
            <button
              key={s.id}
              type="button"
              onClick={() => (canClick ? onStepClick?.(s.id) : null)}
              disabled={!canClick}
              className={cn(
                'group flex min-w-0 flex-1 items-center gap-3 rounded-2xl px-3 py-2 text-left',
                'transition-colors duration-200',
                canClick ? 'hover:bg-slate-50' : 'cursor-default',
                isCurrent ? 'bg-emerald-50/70' : 'bg-transparent',
                !canClick ? 'disabled:opacity-100' : ''
              )}
              aria-current={isCurrent ? 'step' : undefined}
            >
              <span
                className={cn(
                  'grid h-9 w-9 place-items-center rounded-xl border text-sm font-bold',
                  isCompleted
                    ? 'border-emerald-200 bg-emerald-500 text-white'
                    : isCurrent
                      ? 'border-emerald-200 bg-white text-emerald-700'
                      : 'border-slate-200 bg-white text-slate-600'
                )}
              >
                {isCompleted ? (
                  <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                    <path
                      fillRule="evenodd"
                      d="M16.704 5.292a1 1 0 01.004 1.412l-7.5 7.6a1 1 0 01-1.43.01l-3.5-3.5a1 1 0 011.414-1.414l2.786 2.786 6.79-6.88a1 1 0 011.432-.014z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  s.id
                )}
              </span>
              <span className="min-w-0">
                <span
                  className={cn(
                    'block truncate text-sm font-bold',
                    isCurrent ? 'text-slate-900' : isCompleted ? 'text-slate-900' : 'text-slate-700'
                  )}
                >
                  {s.title}
                </span>
                <span className="block truncate text-xs text-slate-500">{s.subtitle}</span>
              </span>
            </button>
          )
        })}
      </div>

      <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100">
        <div
          className="h-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-[width] duration-300"
          style={{ width: `${progressPct}%` }}
        />
      </div>
    </div>
  )
}

function WizardSection({ title, description, children, className }) {
  return (
    <div className={cn('rounded-2xl border border-slate-200/70 bg-white', className)}>
      <div className="border-b border-slate-200/60 px-5 py-4">
        <p className="text-sm font-bold text-slate-900">{title}</p>
        {description ? <p className="mt-1 text-sm text-slate-600">{description}</p> : null}
      </div>
      <div className="px-5 py-5">{children}</div>
    </div>
  )
}

function ChoiceCard({ title, description, icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group w-full rounded-2xl border border-slate-200 bg-white p-4 text-left',
        'shadow-sm shadow-slate-900/5',
        'transition-all duration-200 ease-out',
        'hover:-translate-y-[1px] hover:border-slate-300 hover:shadow-md',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-2'
      )}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-700">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-900">{title}</p>
          <p className="mt-1 text-sm text-slate-600">{description}</p>
        </div>
        <span className="mt-1 text-slate-400 transition-colors group-hover:text-slate-600">
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
            <path
              fillRule="evenodd"
              d="M7.21 14.77a.75.75 0 01.02-1.06L10.94 10 7.23 6.29a.75.75 0 111.06-1.06l4.24 4.24a.75.75 0 010 1.06l-4.24 4.24a.75.75 0 01-1.06.02z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </div>
    </button>
  )
}

const CreateProject = ({ onSuccess, onCancel, preselectedTopicId, preselectedTopic }) => {
  const [step, setStep] = useState(preselectedTopicId && preselectedTopic ? 2 : 1)
  const [topicSource, setTopicSource] = useState(preselectedTopicId ? 'saved' : null)
  const [savedTopics, setSavedTopics] = useState([])
  const [loadingSaved, setLoadingSaved] = useState(false)
  const [selectedTopic, setSelectedTopic] = useState(preselectedTopic || null)

  // Manual entry
  const [topicIdea, setTopicIdea] = useState("")
  const [featuresText, setFeaturesText] = useState("")
  const [additionalNotes, setAdditionalNotes] = useState("")

  // School & department
  const [useProfileDetails, setUseProfileDetails] = useState(true)
  const [profile, setProfile] = useState(null)
  const [schools, setSchools] = useState([])
  const [courses, setCourses] = useState([])
  const [schoolId, setSchoolId] = useState("")
  const [courseId, setCourseId] = useState("")
  const [school, setSchool] = useState("")
  const [department, setDepartment] = useState("")

  // Final step
  const [documentationType, setDocumentationType] = useState('Undergraduate Final Year Project')
  const [existingTopicProjects, setExistingTopicProjects] = useState([])
  const [createLoading, setCreateLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (preselectedTopicId && preselectedTopic) {
      setTopicSource('saved')
      setSelectedTopic(preselectedTopic)
    }
  }, [preselectedTopicId, preselectedTopic])

  useEffect(() => {
    const topicId = selectedTopic?._id
    if (!topicId) {
      setExistingTopicProjects([])
      return
    }
    projectsApi.list({ topic: topicId })
      .then((res) => {
        setExistingTopicProjects(res?.data?.data?.projects || [])
      })
      .catch(() => setExistingTopicProjects([]))
  }, [selectedTopic?._id])

  const duplicateProject = useMemo(() => {
    if (!selectedTopic?._id) return null
    return existingTopicProjects.find((p) => p.documentationType === documentationType) || null
  }, [selectedTopic?._id, existingTopicProjects, documentationType])

  useEffect(() => {
    if (topicSource === 'saved' && savedTopics.length === 0) {
      setLoadingSaved(true)
      topicsApi.list({ mine: 'true', limit: 100 })
        .then(res => {
          if (res?.data?.data?.topics) setSavedTopics(res.data.data.topics)
        })
        .catch(() => {})
        .finally(() => setLoadingSaved(false))
    }
  }, [topicSource, savedTopics.length])

  useEffect(() => {
    if (step >= 2) {
      schoolsApi.list().then(res => setSchools(res?.data?.data || [])).catch(() => setSchools([]))
      coursesApi.list().then(res => setCourses(res?.data?.data || [])).catch(() => setCourses([]))
    }
  }, [step])

  useEffect(() => {
    if (!schoolId) return
    const s = schools.find(x => x._id === schoolId)
    if (s?.name) setSchool(s.name)
  }, [schoolId, schools])

  useEffect(() => {
    setDepartment("")
    setCourseId("")
  }, [schoolId])

  useEffect(() => {
    if (useProfileDetails || step >= 2) {
      authApi.me()
        .then(res => {
          if (res?.data?.data) {
            const p = res.data.data
            setProfile(p)
            if (useProfileDetails) {
              setSchool(p.school || "")
              setDepartment(p.department || "")
            }
          }
        })
        .catch(() => {})
    }
  }, [useProfileDetails, step])

  useEffect(() => {
    if (!useProfileDetails || !profile || courses.length === 0) return
    const dept = String(profile.department || '').trim().toLowerCase()
    if (dept && !courseId) {
      const match = courses.find((c) => String(c.name || '').trim().toLowerCase() === dept)
      if (match) {
        setCourseId(match._id)
        setDepartment(match.name || profile.department)
      }
    }
    const schoolName = String(profile.school || '').trim().toLowerCase()
    if (schoolName && schools.length > 0 && !schoolId) {
      const schoolMatch = schools.find((s) => String(s.name || '').trim().toLowerCase() === schoolName)
      if (schoolMatch) setSchoolId(schoolMatch._id)
    }
  }, [useProfileDetails, profile, courses, schools, courseId, schoolId])

  const handleSelectTopic = (t) => {
    setSelectedTopic(t)
  }

  const handleUseProfileToggle = (checked) => {
    setUseProfileDetails(checked)
    if (checked && profile) {
      setSchool(profile.school || "")
      setDepartment(profile.department || "")
      setSchoolId("")
      setCourseId("")
    } else if (!checked) {
      setSchoolId("")
      setCourseId("")
      setSchool("")
      setDepartment("")
    }
  }

  const canProceedStep1 = () => {
    if (topicSource === 'saved') return !!selectedTopic
    if (topicSource === 'manual') return topicIdea.trim().length >= 3
    return false
  }

  const canProceedStep2 = () => !!courseId

  const getTitleAndDescription = () => {
    if (selectedTopic) {
      const desc = selectedTopic.description || selectedTopic.summary || ""
      const feat = (selectedTopic.features || []).length
        ? "\n\nFeatures:\n" + (selectedTopic.features || []).join("\n")
        : ""
      let description = (desc + feat).trim() || selectedTopic.title
      if (description.length < 10) description = selectedTopic.title + " - Project description."
      return {
        title: selectedTopic.title,
        description
      }
    }
    const featList = featuresText.trim()
      .split("\n")
      .map(s => s.trim())
      .filter(Boolean)
    const desc = topicIdea
    const featPart = featList.length ? "\n\nFeatures:\n" + featList.join("\n") : ""
    const notesPart = additionalNotes.trim() ? "\n\nAdditional notes:\n" + additionalNotes.trim() : ""
    let description = (desc + featPart + notesPart).trim() || topicIdea.trim()
    if (description.length < 10) description = topicIdea.trim() + " - Project description."
    return {
      title: topicIdea.trim(),
      description,
      features: featList,
      additionalNotes: additionalNotes.trim() || undefined
    }
  }

  const selectedCourseName = courses.find((c) => c._id === courseId)?.name || department

  const handleCreate = async () => {
    const { title, description, features, additionalNotes: notes } = getTitleAndDescription()
    if (title.length < 3 || description.length < 10) {
      setError("Title must be at least 3 characters and description at least 10.")
      return
    }
    if (!courseId) {
      setError("Select your department/course — generation uses templates configured for that course.")
      return
    }
    if (duplicateProject) {
      setError('A project already exists for this topic and documentation type.')
      return
    }
    setCreateLoading(true)
    setError("")
    try {
      const payload = {
        title,
        description,
        // Project type is not collected in the finalize step; send a safe backend default.
        projectType: 'Other',
        // The backend currently expects a documentationType for project creation.
        // For proposal we still store documentationType as an academic level proxy, and also send proposalType.
        documentationType,
        generateFor: 'DOCUMENTATION',
        ...(selectedTopic?._id ? { topic: selectedTopic._id } : {}),
        ...(features?.length ? { features } : {}),
        ...(notes ? { additionalNotes: notes } : {}),
        ...(schoolId ? { schoolId } : {}),
        courseId,
        ...(school.trim() ? { school: school.trim() } : {}),
        ...(department.trim() ? { department: department.trim() } : {})
      }
      const res = await projectsApi.create(payload)
      if (res?.data?.data) {
        onSuccess?.(res.data.data)
      } else {
        setError(res?.data?.message || "Failed to create project")
      }
    } catch (e) {
      const code = e?.response?.data?.code
      const projectId = e?.response?.data?.data?.projectId
      if (code === 'DUPLICATE_TOPIC_DOCUMENTATION_TYPE' && projectId) {
        setError('A project already exists for this topic and documentation type.')
      } else {
        setError(e?.response?.data?.message || "Failed to create project")
      }
    } finally {
      setCreateLoading(false)
    }
  }

  return (
    <div className="flex w-full max-w-2xl flex-col">
      <div className="mb-6">
        <h2 className="text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl">Create project</h2>
        <p className="mt-1 text-sm text-slate-600">
          A clean 3‑step flow. You can always go back before creating.
        </p>
      </div>

      <Stepper currentStep={step} onStepClick={(s) => setStep(s)} />

      {/* Step 1: Topic */}
      {step === 1 && (
        <WizardSection
          title="Step 1 — Choose your topic"
          description="Start from a saved idea or write a new topic with optional features."
        >
          {!topicSource ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ChoiceCard
                title="Use a saved idea"
                description="Pick from your previously saved topic ideas."
                onClick={() => setTopicSource('saved')}
                icon={
                  <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                    <path d="M4 3.5A1.5 1.5 0 015.5 2h6A1.5 1.5 0 0113 3.5V4h1.5A1.5 1.5 0 0116 5.5v11A1.5 1.5 0 0114.5 18h-9A1.5 1.5 0 014 16.5V3.5zM13 5.5V4H5.5a.5.5 0 00-.5.5v12a.5.5 0 00.5.5h9a.5.5 0 00.5-.5v-11a.5.5 0 00-.5-.5H13z" />
                  </svg>
                }
              />
              <ChoiceCard
                title="Enter manually"
                description="Write your topic, list features, and add notes."
                onClick={() => setTopicSource('manual')}
                icon={
                  <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                    <path d="M4 4.5A2.5 2.5 0 016.5 2h4A2.5 2.5 0 0113 4.5V5h.5A2.5 2.5 0 0116 7.5v8A2.5 2.5 0 0113.5 18h-7A2.5 2.5 0 014 15.5v-11zM12 5v-.5A1.5 1.5 0 0010.5 3h-4A1.5 1.5 0 005 4.5v11A1.5 1.5 0 006.5 17h7a1.5 1.5 0 001.5-1.5v-8A1.5 1.5 0 0013.5 6H13a1 1 0 01-1-1z" />
                  </svg>
                }
              />
            </div>
          ) : topicSource === 'saved' ? (
            <div>
              <div className="mb-4 flex items-center justify-between gap-3">
                <Button type="button" variant="ghost" size="sm" onClick={() => setTopicSource(null)}>
                  ← Change
                </Button>
                <span className="text-xs text-slate-500">Select one to continue</span>
              </div>
              {loadingSaved ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  Loading saved ideas…
                </div>
              ) : (
                <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                  {savedTopics.length === 0 ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                      No saved topics yet. You can enter a topic manually, or generate ideas from Topic Ideas first.
                    </div>
                  ) : (
                    savedTopics.map((t) => (
                      (() => {
                        const isSelected = selectedTopic?._id && selectedTopic._id === t._id

                        return (
                      <Card
                        key={t._id}
                        onClick={() => handleSelectTopic(t)}
                        className={cn(
                          'cursor-pointer border-slate-200/70 transition-all',
                          'hover:border-slate-300 hover:shadow-md',
                          isSelected ? 'border-emerald-300 bg-emerald-50 shadow-md ring-1 ring-emerald-200' : ''
                        )}
                      >
                        <CardBody className="flex items-start justify-between gap-3 px-4 py-3">
                          <div className="min-w-0">
                            <p className={cn("truncate font-semibold", isSelected ? "text-emerald-800" : "text-slate-900")}>{t.title}</p>
                            {(t.summary || t.description) ? (
                              <p className={cn("mt-1 line-clamp-1 text-sm", isSelected ? "text-emerald-700" : "text-slate-600")}>{t.summary || t.description}</p>
                            ) : null}
                          </div>
                          <span
                            className={cn(
                              "mt-0.5 shrink-0 rounded-xl border px-2 py-1 text-xs font-semibold",
                              isSelected
                                ? "border-emerald-200 bg-emerald-100 text-emerald-800"
                                : "border-slate-200 bg-white text-slate-600"
                            )}
                          >
                            {isSelected ? 'Selected' : 'Choose'}
                          </span>
                        </CardBody>
                      </Card>
                        )
                      })()
                    ))
                  )}
                </div>
              )}

              <div className="mt-5 flex items-center justify-end gap-2">
                <Button type="button" variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
                <Button type="button" disabled={!canProceedStep1()} onClick={() => setStep(2)}>
                  Continue
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <Button type="button" variant="ghost" size="sm" onClick={() => setTopicSource(null)}>
                  ← Change
                </Button>
                <span className="text-xs text-slate-500">All fields autosave on this page</span>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <Field label="Topic idea" hint="Minimum 3 characters.">
                  <Input
                    type="text"
                    placeholder="e.g. E‑commerce platform for local artisans"
                    value={topicIdea}
                    onChange={(e) => setTopicIdea(e.target.value)}
                  />
                </Field>
                <Field label="Features (optional)" hint="One per line.">
                  <Textarea
                    placeholder={"User authentication\nProduct catalog\nShopping cart\nPayment integration"}
                    rows={4}
                    value={featuresText}
                    onChange={(e) => setFeaturesText(e.target.value)}
                  />
                </Field>
                <Field label="Additional notes (optional)">
                  <Textarea
                    placeholder="Any extra context or requirements…"
                    rows={2}
                    value={additionalNotes}
                    onChange={(e) => setAdditionalNotes(e.target.value)}
                  />
                </Field>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
                <Button type="button" disabled={!canProceedStep1()} onClick={() => setStep(2)}>
                  Continue
                </Button>
              </div>
            </div>
          )}
        </WizardSection>
      )}

      {/* Step 2: School & Department */}
      {step === 2 && (
        <WizardSection
          title="Step 2 — School & department"
          description="Use your profile details, or select school and department manually."
        >
          {getTitleAndDescription().title ? (
            <Card className="mb-4 border-slate-200/70 bg-slate-50">
              <CardBody className="px-4 py-4">
                <p className="text-xs font-semibold text-slate-500">Topic</p>
                <p className="mt-1 text-sm font-bold text-slate-900">{getTitleAndDescription().title}</p>
              </CardBody>
            </Card>
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button type="button" variant="ghost" size="sm" onClick={() => setStep(1)}>
              ← Back
            </Button>
            <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
              <input
                type="checkbox"
                checked={useProfileDetails}
                onChange={(e) => handleUseProfileToggle(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500/30"
              />
              <span className="text-sm font-semibold text-slate-800">Use my profile details</span>
            </label>
          </div>

          {useProfileDetails ? (
            <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-xs font-semibold text-emerald-700">Using profile details</p>
              <p className="mt-1 text-sm font-semibold text-emerald-900">
                {[profile?.school, profile?.department].filter(Boolean).join(' · ') || 'Your profile (school/department not set)'}
              </p>
              <p className="mt-1 text-sm text-emerald-800">
                You can update school/department in Account settings. You still need to pick the course below for templates.
              </p>
            </div>
          ) : (
            <div className="mt-4">
              <Field label="School (optional)">
                <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
                  <option value="">Select school</option>
                  {schools.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          )}

          <div className="mt-4">
            <Field label="Department / Course" hint="Required — documentation is generated from templates set up for this course.">
              <Select
                value={courseId}
                onChange={(e) => {
                  const nextCourseId = e.target.value
                  setCourseId(nextCourseId)
                  const c = courses.find(x => x._id === nextCourseId)
                  setDepartment(c?.name || "")
                }}
              >
                <option value="">Select department / course</option>
                {courses.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            {(school || department) ? (
              <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                <p className="text-xs font-semibold text-emerald-700">Current selection</p>
                <p className="mt-1 text-sm font-semibold text-emerald-900">
                  {[school, department].filter(Boolean).join(' · ')}
                </p>
              </div>
            ) : null}
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" disabled={!canProceedStep2()} onClick={() => setStep(3)}>
              Continue
            </Button>
          </div>
        </WizardSection>
      )}

      {/* Step 3: Finalize & Create */}
      {step === 3 && (
        <WizardSection
          title="Step 3 — Finalize"
          description="Confirm your project documentation type, then create."
        >
          <div className="flex items-center justify-between gap-3">
            <Button type="button" variant="ghost" size="sm" onClick={() => setStep(2)}>
              ← Back
            </Button>
            <span className="text-xs text-slate-500">Ready to create</span>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4">
            <Card className="border-slate-200/70 bg-slate-50">
              <CardBody className="px-4 py-4">
                <p className="text-xs font-semibold text-slate-500">Topic</p>
                <p className="mt-1 text-sm font-bold text-slate-900">{getTitleAndDescription().title}</p>
              </CardBody>
            </Card>

            <Card className="border-emerald-200 bg-emerald-50">
              <CardBody className="px-4 py-4">
                <p className="text-xs font-semibold text-emerald-700">Final selection</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedCourseName ? (
                    <span className="rounded-xl border border-emerald-200 bg-white px-2 py-1 text-xs font-semibold text-emerald-800">
                      {[school, selectedCourseName].filter(Boolean).join(' · ')}
                    </span>
                  ) : null}
                  <span className="rounded-xl border border-emerald-200 bg-white px-2 py-1 text-xs font-semibold text-emerald-800">
                    {documentationType}
                  </span>
                </div>
              </CardBody>
            </Card>

            <Field label="Project documentation type" hint="Used to tailor structure and formatting.">
              <Select value={documentationType} onChange={(e) => setDocumentationType(e.target.value)}>
                {DOCUMENTATION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Field>

            {duplicateProject ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                <p>A project already exists for this topic with documentation type &ldquo;{documentationType}&rdquo;.</p>
                <Link
                  to={`/dashboard/project?projectId=${encodeURIComponent(String(duplicateProject._id))}`}
                  className="mt-2 inline-block font-semibold text-emerald-700 hover:text-emerald-800"
                >
                  Open existing project →
                </Link>
              </div>
            ) : null}

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}
          </div>

          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" loading={createLoading} disabled={!!duplicateProject} onClick={handleCreate}>
              Create project
            </Button>
          </div>
        </WizardSection>
      )}
    </div>
  )
}

export default CreateProject
